import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useWebSocket } from "@/lib/ws";
import { RefreshCw, TrendingUp, TrendingDown, Minus, Activity as ActivityIcon } from "lucide-react";
import { toast } from "sonner";

const TrendPill = ({ regime }) => {
  const map = {
    bullish: { c: "#10b981", b: "rgba(16, 185, 129, 0.15)", Icon: TrendingUp, label: "BULL" },
    bearish: { c: "#ef4444", b: "rgba(239, 68, 68, 0.15)", Icon: TrendingDown, label: "BEAR" },
    ranging: { c: "#9ca3af", b: "rgba(156, 163, 175, 0.15)", Icon: Minus, label: "RANGE" },
  };
  const t = map[regime] || map.ranging;
  const { Icon } = t;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-bold border"
      style={{ background: t.b, borderColor: t.c, color: t.c }}
      data-testid={`trend-${regime}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2.5} /> {t.label}
    </span>
  );
};

const SessionPill = ({ session }) => {
  const color = session === "London-NY-Overlap" ? "#f59e0b"
    : session === "NY" ? "#3b82f6"
    : session === "London" ? "#8b5cf6"
    : session === "Asian" ? "#0ea5e9"
    : "#6b7280";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[9px] font-mono font-semibold border"
      style={{ borderColor: color, color }}
    >
      {session}
    </span>
  );
};

const SymbolCard = ({ card, onClick }) => {
  const isBuy = card.top_signal?.direction === "BUY";
  return (
    <button
      onClick={onClick}
      data-testid={`scanner-card-${card.symbol}`}
      className="vx-card p-4 text-left hover:bg-[#1f2937] transition-colors group active:scale-[0.99]"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono font-bold text-sm">{card.symbol}</div>
          <div className="text-[10px] text-[#6b7280] font-mono">{card.timeframe || "—"}</div>
        </div>
        <TrendPill regime={card.trend_regime} />
      </div>
      <div className="mt-3 flex items-end justify-between">
        <div className="font-mono text-xl font-bold tracking-tight">
          {card.last_close !== null && card.last_close !== undefined ? Number(card.last_close).toFixed(5) : "—"}
        </div>
        <div className="text-[10px] text-[#6b7280] font-mono">
          ATR {card.atr ? Number(card.atr).toFixed(5) : "—"}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-[10px] text-[#9ca3af]">
        <SessionPill session={card.session} />
        <span className="font-mono">{card.signals_24h} sig · 24h</span>
      </div>
      {card.top_signal ? (
        <div
          className="mt-3 pt-3 border-t border-[#1f2937] flex items-center justify-between"
          data-testid={`scanner-top-signal-${card.symbol}`}
        >
          <div className="flex items-center gap-2">
            <span
              className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold"
              style={{
                background: isBuy ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
                color: isBuy ? "#10b981" : "#ef4444",
                border: `1px solid ${isBuy ? "#10b981" : "#ef4444"}`,
              }}
            >
              {card.top_signal.direction}
            </span>
            <span className="text-[10px] font-mono text-[#9ca3af]">{card.top_signal.pattern_type}</span>
          </div>
          <span className="font-mono text-xs font-bold text-[#3b82f6]">{card.top_signal.confidence_score}</span>
        </div>
      ) : (
        <div className="mt-3 pt-3 border-t border-[#1f2937] text-[10px] text-[#6b7280] font-mono">No active signals</div>
      )}
    </button>
  );
};

export default function Scanner() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/scanner/overview");
      setCards(r.data.scanner || []);
    } catch (e) {
      toast.error("Failed to load scanner");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  useWebSocket((msg) => {
    if (msg.type === "signal.new" || msg.type === "signal.consumed" || msg.type === "price.tick") {
      // Debounced reload — fire load after small delay
      load();
    }
  });

  return (
    <div className="space-y-6" data-testid="scanner-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="vx-label">Scanner</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mt-1">Multi-Symbol Radar</h1>
          <p className="text-sm text-[#9ca3af] mt-1">Every symbol with EA activity in the last 24 hours.</p>
        </div>
        <button
          onClick={load}
          data-testid="scanner-refresh"
          className="h-9 px-3 rounded-md border border-[#1f2937] hover:bg-[#1f2937] text-xs font-mono flex items-center gap-2 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {cards.length === 0 ? (
        <div className="vx-card p-12 text-center" data-testid="scanner-empty">
          <ActivityIcon className="w-8 h-8 mx-auto text-[#3b82f6] opacity-50" />
          <div className="mt-3 font-heading text-lg font-bold">No symbols tracked yet</div>
          <p className="text-sm text-[#9ca3af] mt-1">Connect your EA and push a few bars to populate the scanner.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {cards.map((c) => (
            <SymbolCard key={c.symbol} card={c} onClick={() => navigate(`/signals?symbol=${encodeURIComponent(c.symbol)}`)} />
          ))}
        </div>
      )}
    </div>
  );
}
